import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usePotwierdzenie } from '@/components/OknoPotwierdzenia';
import { Download, Loader2, Wrench } from 'lucide-react';
import { useCallback, useState } from 'react';
import { OdczytTabeli } from '@/components/OdczytTabeli';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { wyrownajWszystkie, type WynikOdczytuRozjazdu } from '@/lib/machines-api';
import { downloadBlob } from '@/lib/export';
import { nazwaPlikuRozjazdu, raportRozjazdu } from '@/lib/raport-rozjazdu';
import { grupyTresci, opisRozjazdu } from '@/lib/rozjazd-opis';
import type { StanWdrozenia } from '@/lib/wdrozenie-api';

/**
 * CO DOKŁADNIE JEST ROZJECHANE — okno przy sprawdzeniu „Konfiguracja maszyn zgodna".
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ ZGŁOSZONE Z WDROŻENIA IKEA (01.09.2026): „możemy jedynie kliknąć synchronizację bębnów
 *  i wierzyć, że się nie rozjebie (…) fajnie jakby było porównanie, co jest rozjechane". Portal
 *  miał w odpowiedzi kreatora wszystko — tabelę, automat, liczby wierszy i skróty treści z każdego
 *  węzła — i tego nie rysował.
 *
 *  ⚠️ PRZY ROZJEŹDZIE TREŚCI POKAZUJEMY GRUPY SKRÓTÓW, NIE LICZBY. Pierwsza wersja wypisywała
 *  „węzeł → liczba wierszy" dla trzynastu węzłów, czyli trzynaście razy TĘ SAMĄ liczbę — bo przy
 *  rozjeździe treści liczby zgadzają się z definicji. Informację niesie skrót: węzły o tym samym
 *  skrócie mają identyczną treść, więc grupy mówią, KTÓRY węzeł odstaje.
 *
 *  ⚠️ NIE PISZEMY „TEGO NIE DA SIĘ NAPRAWIĆ". Pierwsza wersja tak twierdziła i była to nieprawda:
 *  rozjazd treści prostuje „Ustaw pozostałe dokładnie tak samo" (tryb wyłączności), co zgłaszający
 *  sprawdził na innej instancji. Okno nazywa więc przycisk, który REALNIE naprawia dany rodzaj —
 *  i pozwala odpalić go dla wszystkich automatów naraz.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function RozjazdSzczegoly({
  groupId,
  d,
  onClose,
}: {
  groupId: string;
  d: Pick<StanWdrozenia, 'rozjazdy' | 'automaty' | 'doWyrownania' | 'tabeleWyrownania'> & {
    /** Do nagłówka pliku — bez nazwy wyspy trzeci taki plik na dysku jest nierozpoznawalny. */
    wyspa: Pick<StanWdrozenia['wyspa'], 'name' | 'code'>;
  };
  onClose: () => void;
}) {
  const { t } = useI18n();
  const { zapytaj, oknoPytania } = usePotwierdzenie();
  const toast = useToast();
  const qc = useQueryClient();
  const [wynik, setWynik] = useState<Awaited<ReturnType<typeof wyrownajWszystkie>> | null>(null);
  /**
   * ═════════════════════════════════════════════════════════════════════
   *  ODCZYTY Z BAZ — trzymane TYLKO po to, żeby zapisany plik mówił to samo, co ekran.
   *
   *  ⚠️⚠️ SAM ODCZYT ROBI `OdczytTabeli` (wydzielony 14.09.2026, bo ten sam blok był potrzebny
   *  drugi raz — do kadry, której czujnik nie widzi). Okno przestało więc pilnować, kto na co
   *  czeka; zostawia sobie wyłącznie WYNIK, bo plik z rozjazdami składa się z tej samej
   *  odpowiedzi, którą ekran właśnie narysował. Drugie zapytanie mogłoby trafić w inny cykl agenta.
   * ═════════════════════════════════════════════════════════════════════
   */
  const [odczyty, setOdczyty] = useState<Record<string, WynikOdczytuRozjazdu>>({});
  /** ⚠️ `useCallback`, żeby nie przerysowywać odczytów przy każdym renderze okna. */
  const zapamietajOdczyt = useCallback((tabela: string, vmid: number | null, w: WynikOdczytuRozjazdu) => {
    setOdczyty((p) => ({ ...p, [`${vmid ?? ''}|${tabela}`]: w }));
  }, []);

  /** Grupujemy po automacie, bo tak wygląda naprawa: źródłem jest właściciel tego automatu. */
  const poAutomacie = new Map<number, StanWdrozenia['rozjazdy']>();
  for (const r of d.rozjazdy) {
    const lista = poAutomacie.get(r.vmid);
    if (lista === undefined) poAutomacie.set(r.vmid, [r]);
    else lista.push(r);
  }
  const vmidy = [...poAutomacie.keys()].sort((a, b) => a - b);
  const opisAutomatu = (vmid: number) => d.automaty?.find((a) => a.vmid === vmid);
  const niestabilnych = d.rozjazdy.filter((r) => !r.stabilny).length;

  const wszystkie = useMutation({
    mutationFn: () => wyrownajWszystkie(groupId, true),
    onSuccess: (w) => {
      setWynik(w);
      toast.success(
        t('rozj.wszystkieWynik')
          .replace('{ok}', String(w.ok))
          .replace('{razem}', String(w.razem))
          .replace('{zmian}', w.zmian.toLocaleString('pl')),
      );
      void qc.invalidateQueries({ queryKey: ['wdrozenie'] });
      void qc.invalidateQueries({ queryKey: ['changes'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Nie udało się wyrównać'),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      {oknoPytania}
      <DialogContent
        title={t('rozj.tytul')}
        description={t('rozj.podtytul')
          .replace('{tabel}', String(d.rozjazdy.length))
          .replace('{automatow}', String(vmidy.length))}
      >
        <div className="max-h-[60vh] space-y-3 overflow-y-auto">
          {niestabilnych > 0 && (
            <p className="text-[11px] text-muted-foreground">
              {t('rozj.dojezdzaInfo').replace('{n}', String(niestabilnych))}
            </p>
          )}

          {vmidy.map((vmid) => {
            const a = opisAutomatu(vmid);
            /** `doWyrownania` = da się naprawić SŁABSZYM trybem („Uzupełnij pozostałe maszyny"). */
            const brakCalkowity = d.doWyrownania?.includes(vmid) ?? false;
            /**
             * ⚠️⚠️ KTÓRE ROZJECHANE TABELE TEGO AUTOMATU SĄ W ZASIĘGU PRZYCISKÓW. Puste znaczy
             * „żaden przycisk portalu tu nie pomoże" — i wtedy zdanie pod listą nie ma prawa
             * nazywać przycisku wyrównania.
             */
            const poWyrownaniu = poAutomacie
              .get(vmid)!
              .filter((r) => d.tabeleWyrownania === undefined || d.tabeleWyrownania.includes(r.tabela));
            const wynikTego = wynik?.automaty.find((x) => x.vmid === vmid);
            return (
              <section key={vmid} className="space-y-1.5 rounded border border-border p-2.5">
                <p className="text-sm font-medium">
                  {a?.nazwa ?? t('rozj.bezNazwy')} <span className="text-muted-foreground">· VMID {vmid}</span>
                  <span className="ml-2 text-[11px] font-normal text-muted-foreground">
                    {a?.wlascicielWezel == null
                      ? t('rozj.wlascicielNieznany')
                      : t('rozj.wlasciciel').replace('{wezel}', a.wlascicielWezel)}
                  </span>
                </p>

                <ul className="space-y-1.5 text-xs">
                  {poAutomacie.get(vmid)!.map((r) => {
                    const o = opisRozjazdu(r.wezly, r.rodzaj, a?.wlascicielWezel ?? null);
                    const pozaWyrownaniem = d.tabeleWyrownania !== undefined && !d.tabeleWyrownania.includes(r.tabela);
                    return (
                      <li key={r.tabela} className="space-y-0.5">
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          <span className="font-mono font-semibold">{r.tabela}</span>
                          {o.rodzaj === 'TRESC' ? (
                            <>
                              <span className="text-[var(--warning)]">{t('rozj.innaTresc')}</span>
                              {/*
                                ⚠️⚠️ NAZWA KOLUMNY JEST TU CAŁĄ ODPOWIEDZIĄ na pytanie z 01.09.2026:
                                „nie chcę, żeby wyskakiwał błąd, jak ktoś coś wybierze". Stan
                                magazynowy i znaczniki czasu są z porównania WYŁĄCZONE (zmierzona
                                lista w `agent/src/sync/geometria.ts`), więc kolumna, która się tu
                                pokaże, jest różnicą KONFIGURACJI — albo czymś, co trzeba dopisać
                                do tamtej listy. Bez nazwy nie da się tego rozstrzygnąć.
                              */}
                              {r.kolumny !== undefined && r.kolumny.length > 0 ? (
                                <span className="font-mono text-[11px]">
                                  {t('rozj.kolumnyRozne').replace('{kolumny}', r.kolumny.join(', '))}
                                </span>
                              ) : r.innyUklad === true ? (
                                <span className="text-[11px]">{t('rozj.innyUklad')}</span>
                              ) : (
                                <span className="text-[11px] text-muted-foreground">{t('rozj.kolumnyNieznane')}</span>
                              )}
                            </>
                          ) : (
                            <>
                              {o.zrodlo !== null && (
                                <span className="text-muted-foreground">
                                  {t('rozj.zrodlo')
                                    .replace('{wezel}', o.zrodlo.nodeId)
                                    .replace('{wierszy}', String(o.zrodlo.wierszy))}
                                </span>
                              )}
                              {o.grupy.map((g) => (
                                <span key={`brak-${g.brakuje}`} className="text-[var(--destructive)]">
                                  {(g.wcale
                                    ? t('rozj.brakWcale')
                                    : t('rozj.brakuje').replace('{ile}', String(g.brakuje))
                                  ).replace('{wezly}', g.wezly.join(', '))}
                                </span>
                              ))}
                              {o.nadmiar.map((n) => (
                                <span key={`nad-${n.nodeId}`} className="text-[var(--warning)]">
                                  {t('rozj.nadmiar').replace('{ile}', String(n.ponad)).replace('{wezel}', n.nodeId)}
                                </span>
                              ))}
                            </>
                          )}
                          {!r.stabilny && <span className="text-muted-foreground">— {t('rozj.dojezdza')}</span>}
                          {pozaWyrownaniem && (
                            <span className="rounded border border-border px-1 text-[10px] text-muted-foreground">
                              {t('rozj.pozaWyrownaniem')}
                            </span>
                          )}
                        </div>

                        {/*
                          ═════════════════════════════════════════════════════════════════
                           ODPYTANIE BAZ O TEN JEDEN WIERSZ LISTY — zamówione 09.09.2026.

                           ⚠️⚠️ DO TEJ PORY OKNO KOŃCZYŁO SIĘ NA LICZBACH. Czujnik liczy wiersze i skróty,
                           więc najdalej, jak sięgał, było „brakuje 11 u magna2" — a odpowiedź na „których
                           jedenastu" wymagała zalogowania się do każdej bazy w SSMS. Ten przycisk zleca
                           świeży `SELECT` na KAŻDYM węźle z bazą i odejmuje zbiory po kluczu naturalnym.

                           ⚠️ WYNIK PRZYCHODZI PO CYKLU AGENTA — stąd „czekam", a nie kręcące się kółko bez
                           końca. Zlecenie jest jednym zadaniem na węzeł, więc widać też, który węzeł nie
                           odpowiedział — bez tego „brakuje u magna2" mogłoby znaczyć „magna2 milczy".
                          ═════════════════════════════════════════════════════════════════
                        */}
                        <OdczytTabeli groupId={groupId} tabela={r.tabela} vmid={vmid} onWynik={zapamietajOdczyt} />

                        {/*
                          ⚠️ PRZY ROZJEŹDZIE TREŚCI LICZBY SĄ RÓWNE, więc pokazujemy GRUPY SKRÓTÓW —
                          jedyną rzecz, która mówi, który węzeł odstaje od reszty. Przy rozjeździe
                          liczb odwrotnie: liczby są treścią, a skróty i tak muszą się różnić.
                        */}
                        <details className="ml-1">
                          <summary className="cursor-pointer text-[11px] text-muted-foreground">
                            {o.rodzaj === 'TRESC' ? t('rozj.grupyTresci') : t('rozj.wszystkieLiczby')}
                          </summary>
                          {o.rodzaj === 'TRESC' ? (
                            <ul className="mt-1 space-y-0.5 text-[11px]">
                              {grupyTresci(r.wezly).map((g) => (
                                <li key={g.skrot ?? 'brak'}>
                                  <span className="font-mono">
                                    {g.skrot === null
                                      ? t('rozj.grupaBezSkrotu').replace('{wezly}', g.wezly.join(', '))
                                      : t('rozj.grupaTresci')
                                          .replace('{skrot}', g.skrot.slice(0, 8))
                                          .replace('{wezly}', g.wezly.join(', '))}
                                  </span>
                                  {a?.wlascicielWezel != null && g.wezly.includes(a.wlascicielWezel) && (
                                    <span className="text-muted-foreground">{t('rozj.grupaWlasciciela')}</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <ul className="mt-1 space-y-0.5 text-[11px]">
                              {[...r.wezly]
                                .sort((x, y) => y.wierszy - x.wierszy || x.nodeId.localeCompare(y.nodeId))
                                .map((w) => (
                                  <li key={w.nodeId} className="flex gap-2">
                                    <span className="font-mono">{w.nodeId}</span>
                                    <span className="tabular-nums">{w.wierszy}</span>
                                  </li>
                                ))}
                            </ul>
                          )}
                        </details>
                      </li>
                    );
                  })}
                </ul>

                {/*
                  ═════════════════════════════════════════════════════════════
                   CO REALNIE NAPRAWIA TEN ROZJAZD — i to zdanie MUSI się zgadzać z plakietkami wyżej.

                   ⚠️⚠️ POPRAWIONE 09.09.2026. Stało tu „naprawi to Ustaw pozostałe dokładnie tak samo"
                   dla każdego automatu, także wtedy, gdy WSZYSTKIE jego rozjechane tabele miały obok
                   plakietkę „poza wyrównaniem". Ekran obiecywał więc naprawę, której ten przycisk
                   nie umie zrobić — zmierzone: przyciski dotykają SIEDMIU tabel geometrii bębna,
                   a czujnik porównuje DWANAŚCIE.
                  ═════════════════════════════════════════════════════════════
                */}
                <p className="text-[11px] text-muted-foreground">
                  {poWyrownaniu.length === 0
                    ? t('rozj.tylkoPozaWyrownaniem')
                    : brakCalkowity
                      ? t('rozj.wyrownaniePomoze')
                      : t('rozj.wyrownanieNiePomoze')}
                </p>

                {wynikTego !== undefined && (
                  <p
                    className={
                      wynikTego.ok ? 'text-[11px] text-[var(--success)]' : 'text-[11px] text-[var(--destructive)]'
                    }
                  >
                    {wynikTego.ok ? `+${wynikTego.zmian}` : wynikTego.blad}
                  </p>
                )}
              </section>
            );
          })}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
          {/*
            ═════════════════════════════════════════════════════════════
             PLIK Z ROZJAZDAMI — zamówione 09.09.2026: „czy możemy tutaj zrobić plik z dokładnymi
             znalezionymi rozjazdami?".

             ⚠️ SKŁADAMY GO Z TEJ SAMEJ ODPOWIEDZI, którą rysuje to okno — nie z drugiego odczytu.
             Plik i ekran muszą mówić to samo; drugie zapytanie mogłoby trafić w inny cykl agenta.
            ═════════════════════════════════════════════════════════════
          */}
          <Button
            type="button"
            variant="outline"
            disabled={d.rozjazdy.length === 0}
            onClick={() => {
              const kiedy = new Date();
              downloadBlob(
                new Blob([raportRozjazdu(d, kiedy, odczyty)], { type: 'text/plain;charset=utf-8' }),
                nazwaPlikuRozjazdu(d.wyspa.code, kiedy),
              );
            }}
          >
            <Download className="size-4" />
            {t('rozj.zapiszPlik')}
          </Button>
          {/*
            ⚠️⚠️ TO JEST PRZYCISK Z ZGŁOSZENIA: „potrzebujemy przycisku, który pociągnie aktualne
            stany z maszyn na mastera". Dla KAŻDEGO automatu portal bierze obraz jego WŁAŚCICIELA
            i rozsyła go na pozostałe węzły w trybie wyłączności — czyli tym, który prostuje treść.
          */}
          <Button
            type="button"
            disabled={wszystkie.isPending || vmidy.length === 0}
            onClick={async () => {
              const tytul = t('rozj.wszystkieAutomaty').replace('{n}', String(vmidy.length));
              const tresc = t('rozj.wszystkiePytanie').replace('{n}', String(vmidy.length));
              if (await zapytaj({ tytul, tresc, etykietaPotwierdz: tytul })) wszystkie.mutate();
            }}
          >
            {wszystkie.isPending ? <Loader2 className="size-4 animate-spin" /> : <Wrench className="size-4" />}
            {t('rozj.wszystkieAutomaty').replace('{n}', String(vmidy.length))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
